import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Avatar,
  IconButton,
  Alert
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import { putSchedule, putSchedulePublic } from '../../../lib/connector/scheduleQueries';
import { useToast } from '../ToastProvider/ToastProvider';
import Cookies from 'universal-cookie';
import { INVITE_EXPIRATION_HOURS } from '../../../pages/Join/Join';

interface InviteEmployeeModalProps {
  open: boolean;
  onClose: () => void;
  selectedSchedule: any;
}

export default function InviteEmployeeModal({ open, onClose, selectedSchedule }: InviteEmployeeModalProps) {
  const toast = useToast();
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);

  React.useEffect(() => {
    try {
      const cookies = new Cookies();
      const cookieInviteLink = cookies.get("inviteLink")
      const tokenParts = cookieInviteLink.split("/")[cookieInviteLink.split("/").length-1].split('-');
      const inviteTimestamp = parseInt(tokenParts[1]);
      const currentTimestamp = Date.now();
      const expirationTime = INVITE_EXPIRATION_HOURS * 60 * 60 * 1000;
      if (currentTimestamp - inviteTimestamp < expirationTime) {
        setInviteLink(cookieInviteLink)
      }
    } catch (e) {
      setInviteLink(null)
    }
    }, [])

  const generateInviteLink = () => {
    try {
      const link = `${window.location.origin}/join/${selectedSchedule.userId}/${selectedSchedule.scheduleId}/invite-${Date.now()}`;
      putSchedule(selectedSchedule.userId, selectedSchedule.scheduleId,
        selectedSchedule.name, selectedSchedule.color, link, selectedSchedule.employees,
        selectedSchedule.favorite, selectedSchedule.allowSwapWithoutApproval,
        selectedSchedule.allowPickUpWithoutApproval, selectedSchedule.viewOtherStaff,
        selectedSchedule.activeScheduleFrequency, selectedSchedule.lastActiveScheduleDate, selectedSchedule.availabilityScheduleRequiredTime,
        selectedSchedule.activeScheduleDate, selectedSchedule.lastSubmitted
      ).then(() => {
        putSchedulePublic(selectedSchedule.userId, selectedSchedule.scheduleId,
          selectedSchedule.name, link);
        setInviteLink(link);
        const cookies = new Cookies();
        cookies.set('inviteLink', link, { path: '/' });
        toast.showSuccess('Invite link generated successfully and will expire in 7 days.');
      });
    } catch (error) {
      toast.showError('Failed to generate invite link');
    }

  };

  const copyInviteLink = () => {
    navigator.clipboard.writeText(inviteLink!);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const handleClose = () => {
    // Reset all state
    setLinkCopied(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            <PersonAddIcon />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight="bold">
              Invite Staff
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Add staff members to the schedule - {selectedSchedule?.name || 'your schedule'}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
          {/* Invite Link Section */}
          <Box>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Share Invite Link
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Generate a link that anyone can use to join your team. This link must match what a staff member uses
            </Typography>

            {!inviteLink ? (
              <Button
                variant="outlined"
                fullWidth
                onClick={generateInviteLink}
              >
                Generate Invite Link
              </Button>
            ) : (
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 2,
                    bgcolor: 'grey.50',
                    borderRadius: 1,
                    border: 1,
                    borderColor: 'divider'
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontFamily: 'monospace'
                    }}
                  >
                    {inviteLink}
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={linkCopied ? <CheckIcon /> : <CopyIcon />}
                    onClick={copyInviteLink}
                    color={linkCopied ? "success" : "primary"}
                  >
                    {linkCopied ? 'Copied!' : 'Copy'}
                  </Button>
                </Box>
                <Alert severity="info" sx={{ mt: 2 }}>
                  This link will allow anyone who joins to be added as a staff member to your schedule. It will expire in 7 days.
                </Alert>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, pt: 0 }}>
        <Button onClick={handleClose} variant="outlined">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}